import { useEffect, useState } from "react";
import { ImagePlus, Package, Pencil, Plus, ShoppingCart, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "../lib/api";
import { compressImage } from "../lib/compressImage";
import PinConfirm from "../components/PinConfirm";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const EMPTY_PRODUCT = { name: "", category: "", price: "", harga_modal: "", stock: "", description: "", image_path: "" };

export default function Stok() {
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [prodForm, setProdForm] = useState(EMPTY_PRODUCT);
  const [prodEditId, setProdEditId] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const [pinProdSave, setPinProdSave] = useState(false);
  const [delProdId, setDelProdId] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const API_BASE = api.defaults.baseURL;

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openAddProduct = () => {
    setProdForm(EMPTY_PRODUCT); setProdEditId(null); setImgFile(null); setImgPreview(""); setProdOpen(true);
  };
  const openEditProduct = (p) => {
    setProdForm({ name: p.name, category: p.category || "", price: p.price || "", harga_modal: p.harga_modal ?? "", stock: p.stock ?? "", description: p.description || "", image_path: p.image_path || "" });
    setProdEditId(p.id); setImgFile(null);
    setImgPreview(p.image_path ? `${API_BASE}/products/files/${p.image_path}` : "");
    setProdOpen(true);
  };
  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const blob = await compressImage(f);
      const file = new File([blob], "produk.jpg", { type: "image/jpeg" });
      setImgFile(file);
      setImgPreview(URL.createObjectURL(blob));
      aiRead(file);
    } catch { toast.error("Gagal memproses gambar"); }
  };

  const aiRead = async (file) => {
    if (!file) { toast.error("Pilih foto dulu"); return; }
    setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/products/ai-read", fd);
      const d = r.data || {};
      setProdForm((p) => ({
        ...p,
        name: d.name || p.name,
        category: d.category || p.category,
        price: d.price || p.price,
        harga_modal: d.harga_modal || p.harga_modal,
        description: d.description || p.description,
      }));
      toast.success(`AI membaca: ${d.name || "produk"}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setAiBusy(false); }
  };
  const saveProduct = async () => {
    setBusy(true);
    try {
      let image_path = prodForm.image_path;
      if (imgFile) {
        const fd = new FormData();
        fd.append("file", imgFile);
        const up = await api.post("/products/image", fd);
        image_path = up.data.path;
      }
      const payload = { ...prodForm, price: Number(prodForm.price) || 0, harga_modal: Number(prodForm.harga_modal) || 0, stock: Number(prodForm.stock) || 0, image_path };
      if (prodEditId) await api.put(`/products/${prodEditId}`, payload);
      else await api.post("/products", payload);
      toast.success(prodEditId ? "Produk diperbarui" : `Produk "${prodForm.name}" ditambahkan`);
      setProdOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const delProduct = async () => {
    try { await api.delete(`/products/${delProdId}`); toast.success("Produk dihapus"); setDelProdId(null); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const totalModal = products.reduce((s, p) => s + (p.harga_modal || 0) * (p.stock || 0), 0);
  const totalJual = products.reduce((s, p) => s + (p.price || 0) * (p.stock || 0), 0);

  return (
    <div className="space-y-6" data-testid="stok-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><ShoppingCart className="w-7 h-7 text-emerald-500" /> Stok</h1>
          <p className="text-sm text-muted-foreground mt-1">Katalog alat & material — input barang satu per satu seperti toko online.</p>
        </div>
        <Button onClick={openAddProduct} data-testid="btn-add-product"><Plus className="w-4 h-4 mr-1.5" /> Tambah Produk</Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap text-xs font-mono" data-testid="stok-summary">
        <span className="px-2.5 py-1.5 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400" data-testid="stok-nilai-modal">Nilai modal stok: {rupiah(totalModal)}</span>
        <span className="px-2.5 py-1.5 rounded-lg bg-accent/60 border border-border" data-testid="stok-nilai-jual">Nilai jual stok: {rupiah(totalJual)}</span>
        <span className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-500" data-testid="stok-potensi-profit">Potensi profit: {rupiah(totalJual - totalModal)}</span>
      </div>

      <Card className="bg-card border-border shadow-sm" data-testid="product-catalog-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Package className="w-4 h-4 text-[#F04E23]" /> Katalog Produk ({products.length})</CardTitle></CardHeader>
        <CardContent>
          {!products.length && <div className="text-center py-8 text-muted-foreground text-sm" data-testid="product-empty">Belum ada produk — klik "Tambah Produk" untuk input barang satu per satu seperti toko online.</div>}
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-2">
            {products.map((p) => (
              <div key={p.id} className="rounded-xl border border-border bg-accent/20 overflow-hidden group" data-testid={`product-card-${p.id}`}>
                <div className="pt-2">
                  <div className="w-1/2 mx-auto aspect-square rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                    {p.image_path ? <img src={`${API_BASE}/products/files/${p.image_path}`} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /> : <Package className="w-5 h-5 text-muted-foreground/40" />}
                  </div>
                </div>
                <div className="p-1.5 space-y-0.5">
                  <div className="text-[10px] font-semibold leading-tight line-clamp-2 min-h-[1.6rem]">{p.name}</div>
                  <div className="text-[9px] text-muted-foreground truncate">{p.category || "Tanpa kategori"}</div>
                  <div className="flex items-center justify-between gap-1">
                    <span className="px-1 py-0.5 rounded text-[9px] font-mono font-bold bg-sky-500/10 text-sky-500 border border-sky-500/20 whitespace-nowrap">{rupiah(p.price)}</span>
                    <span className="text-[9px] font-mono text-muted-foreground">stok:{p.stock ?? 0}</span>
                  </div>
                  {p.harga_modal ? <div className="text-[9px] font-mono text-emerald-500 truncate" data-testid={`product-profit-${p.id}`}>profit {rupiah((p.price || 0) - p.harga_modal)}</div> : null}
                  <div className="flex gap-1 pt-0.5">
                    <Button size="icon" variant="outline" className="h-6 flex-1" title="Edit produk" onClick={() => openEditProduct(p)} data-testid={`btn-edit-product-${p.id}`}><Pencil className="w-3 h-3" /></Button>
                    <Button size="icon" variant="outline" className="h-6 w-6 text-rose-500 border-rose-500/30" title="Hapus produk" onClick={() => setDelProdId(p.id)} data-testid={`btn-del-product-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={prodOpen} onOpenChange={setProdOpen}>
        <DialogContent data-testid="dialog-product">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-[#F04E23]" /> {prodEditId ? "Edit Produk" : "Tambah Produk Baru"}</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); setPinProdSave(true); }} className="space-y-3">
            <div className="flex items-center gap-3">
              <label className="w-24 h-24 rounded-2xl border-2 border-dashed border-border bg-accent/30 flex items-center justify-center cursor-pointer overflow-hidden hover:border-[#F04E23]/50 transition-colors relative" data-testid="btn-product-image">
                {imgPreview ? <img src={imgPreview} alt="foto produk" className={`w-full h-full object-cover ${aiBusy ? "opacity-40" : ""}`} /> : <ImagePlus className="w-6 h-6 text-muted-foreground" />}
                {aiBusy && <span className="absolute inset-0 flex items-center justify-center"><Sparkles className="w-5 h-5 text-[#F04E23] animate-pulse" /></span>}
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickImage} data-testid="input-product-image" />
              </label>
              <div className="flex-1 space-y-1.5">
                <div className="text-[11px] text-muted-foreground">Foto produk — klik kotak untuk ambil dari kamera/galeri. <span className="text-[#F04E23] font-semibold">AI otomatis membaca foto & mengisi nama produk.</span></div>
                {imgPreview && (
                  <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]" disabled={aiBusy} onClick={() => aiRead(imgFile)} data-testid="btn-ai-read">
                    <Sparkles className={`w-3 h-3 mr-1 ${aiBusy ? "animate-pulse" : ""} text-[#F04E23]`} /> {aiBusy ? "AI membaca foto..." : "Baca Ulang dengan AI"}
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Nama Produk</Label><Input required value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} placeholder="cth: Router ZTE F609 v3 Second" data-testid="input-product-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Kategori</Label><Input value={prodForm.category} onChange={(e) => setProdForm({ ...prodForm, category: e.target.value })} placeholder="cth: Perangkat Jaringan" data-testid="input-product-category" /></div>
              <div className="space-y-1.5"><Label>Stok</Label><Input type="number" min="0" required value={prodForm.stock} onChange={(e) => setProdForm({ ...prodForm, stock: e.target.value })} data-testid="input-product-stock" /></div>
              <div className="space-y-1.5"><Label>Harga Modal (Rp)</Label><Input type="number" min="0" value={prodForm.harga_modal} onChange={(e) => setProdForm({ ...prodForm, harga_modal: e.target.value })} placeholder="cth: 10000" data-testid="input-product-cost" /></div>
              <div className="space-y-1.5"><Label>Harga Jual (Rp)</Label><Input type="number" min="0" required value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} placeholder="cth: 130000" data-testid="input-product-price" /></div>
            </div>
            {(() => {
              const jual = Number(prodForm.price) || 0;
              const modal = Number(prodForm.harga_modal) || 0;
              if (!jual && !modal) return null;
              const profit = jual - modal;
              return (
                <div className={`rounded-xl border px-3 py-2 text-xs font-mono flex items-center justify-between ${profit >= 0 ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-500" : "border-rose-500/30 bg-rose-500/10 text-rose-400"}`} data-testid="product-profit">
                  <span>Profit per unit</span>
                  <span className="font-bold">{rupiah(profit)}{modal > 0 ? ` (${Math.round((profit / modal) * 100)}%)` : ""}</span>
                </div>
              );
            })()}
            <div className="space-y-1.5"><Label>Deskripsi</Label><Input value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} placeholder="opsional" data-testid="input-product-desc" /></div>
            <Button type="submit" className="w-full" data-testid="btn-save-product">{prodEditId ? "Simpan Perubahan (PIN)" : "Simpan Produk (PIN)"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <PinConfirm open={pinProdSave} onOpenChange={setPinProdSave} title={prodEditId ? "Simpan Perubahan Produk?" : "Tambah Produk Baru?"} description={`Produk "${prodForm.name}" (stok ${prodForm.stock || 0}, ${rupiah(Number(prodForm.price) || 0)}) akan disimpan ke katalog.`} confirmLabel={busy ? "Menyimpan..." : "Ya, Simpan"} onConfirm={saveProduct} testid="pin-product-save" />
      <PinConfirm open={!!delProdId} onOpenChange={() => setDelProdId(null)} title="Hapus Produk?" description="Produk dihapus permanen dari katalog." confirmLabel="Ya, Hapus" destructive onConfirm={delProduct} testid="pin-product-del" />
    </div>
  );
}
